// fallow-ignore-file complexity
import {
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { MapRef } from "react-map-gl/maplibre";
import { Map as MapGL, NavigationControl, Popup } from "react-map-gl/maplibre";
import type { Pin, SearchLocationMode, VenueCard } from "@/api/types";
import { useSupercluster } from "@/hooks/use-supercluster";
import { bboxCenter, longitudeDistance, longitudeSpan, normalizeBbox } from "@/lib/geo-bounds";
import "maplibre-gl/dist/maplibre-gl.css";
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import { readAutoSearchPref, SearchAreaButton, writeAutoSearchPref } from "./search-area-button";
import { computeBboxFromPins, computeInitialFitBbox } from "./select-pins-for-initial-fit";
import { VenueMarkers } from "./venue-markers";
import { VenuePopupCard } from "./venue-popup-card";

const MAP_STYLE = "https://tiles.openfreemap.org/styles/liberty";
const NEUTRAL_VIEW = { longitude: 0, latitude: 30, zoom: 2 };
const FIT_PADDING = { top: 50, bottom: 50, left: 50, right: 50 };
const FIT_MAX_ZOOM = 15;
const FIT_DURATION = 800;
const CLUSTER_RADIUS = 60;
const CLUSTER_MAX_ZOOM = 16;
const VIEWPORT_DRIFT_THRESHOLD = 0.3;
const AUTO_SEARCH_DEBOUNCE_MS = 500;

interface MapPanelProps {
  pins: Pin[];
  venues: VenueCard[];
  hoveredVenueId: string | null;
  onHoverMarker: (id: string | null) => void;
  searchLat?: number;
  searchLng?: number;
  searchBounds?: string;
  searchLocationMode?: SearchLocationMode;
  searchIntent: string;
  isLoading?: boolean;
  onBoundsChange: (bounds: string) => void;
  searchAreaClassName?: string;
}

function parseBounds(boundsStr: string): [number, number, number, number] | null {
  const parts = boundsStr.split(",").map(Number);
  if (parts.length !== 4 || parts.some((part) => !Number.isFinite(part))) return null;
  const [neLat, neLng, swLat, swLng] = parts as [number, number, number, number];
  if (
    Math.abs(neLat) > 90 ||
    Math.abs(swLat) > 90 ||
    Math.abs(neLng) > 180 ||
    Math.abs(swLng) > 180 ||
    swLat > neLat
  )
    return null;
  return [swLng, swLat, neLng, neLat];
}

function serializeBounds(bounds: {
  _ne: { lat: number; lng: number };
  _sw: { lat: number; lng: number };
}): string {
  const [west, south, east, north] = normalizeBbox([
    bounds._sw.lng,
    bounds._sw.lat,
    bounds._ne.lng,
    bounds._ne.lat,
  ]);
  return `${north},${east},${south},${west}`;
}

function getInitialViewState(searchLat?: number, searchLng?: number, searchBounds?: string) {
  if (searchBounds) {
    const parsed = parseBounds(searchBounds);
    if (parsed) {
      return { ...bboxCenter(parsed), zoom: 12 };
    }
  }
  if (searchLat != null && searchLng != null) {
    return { longitude: searchLng, latitude: searchLat, zoom: 12 };
  }
  return NEUTRAL_VIEW;
}

function computeViewportDrift(
  currentCenter: { lat: number; lng: number },
  searchLat?: number,
  searchLng?: number,
  searchBounds?: string,
): number {
  let refLat: number;
  let refLng: number;

  if (searchBounds) {
    const parsed = parseBounds(searchBounds);
    if (parsed) {
      const [swLng, swLat, neLng, neLat] = parsed;
      const center = bboxCenter(parsed);
      refLat = center.latitude;
      refLng = center.longitude;

      const diagonal = Math.hypot(neLat - swLat, longitudeSpan(swLng, neLng));
      if (diagonal === 0) return 0;
      const displacement = Math.hypot(
        currentCenter.lat - refLat,
        longitudeDistance(currentCenter.lng, refLng),
      );
      return displacement / diagonal;
    }
  }

  if (searchLat != null && searchLng != null) {
    refLat = searchLat;
    refLng = searchLng;
    const displacement = Math.hypot(
      currentCenter.lat - refLat,
      longitudeDistance(currentCenter.lng, refLng),
    );
    return displacement > 0.01 ? VIEWPORT_DRIFT_THRESHOLD + 0.01 : 0;
  }

  return 0;
}

// fallow-ignore-next-line complexity
function MapPanel({
  pins,
  venues,
  hoveredVenueId,
  onHoverMarker,
  searchLat,
  searchLng,
  searchBounds,
  searchLocationMode,
  searchIntent,
  isLoading,
  onBoundsChange,
  searchAreaClassName,
}: MapPanelProps) {
  const mapRef = useRef<MapRef>(null);
  const [viewState, setViewState] = useState(
    getInitialViewState(searchLat, searchLng, searchBounds),
  );
  const [showSearchArea, setShowSearchArea] = useState(false);
  const [currentBounds, setCurrentBounds] = useState<string | null>(null);
  const [selectedVenueId, setSelectedVenueId] = useState<string | null>(null);
  const [selectedCluster, setSelectedCluster] = useState<{
    latitude: number;
    longitude: number;
    pinIds: Set<string>;
  } | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState<"unavailable" | "details" | null>(null);
  const [mapAttempt, setMapAttempt] = useState(0);
  const [autoSearch, setAutoSearch] = useState(readAutoSearchPref);

  const shouldFitNextPins = useRef(true);
  const previousSearchRef = useRef({
    searchIntent,
    searchBounds,
    searchLocationMode,
    onBoundsChange,
  });
  const submittedBoundsRef = useRef<string | null>(null);
  const autoSearchTimer = useRef<ReturnType<typeof setTimeout>>(null);
  const resizeMoveEnd = useRef(false);

  const handleAutoSearchChange = useCallback((value: boolean) => {
    setAutoSearch(value);
    writeAutoSearchPref(value);
    if (!value && autoSearchTimer.current) {
      clearTimeout(autoSearchTimer.current);
      autoSearchTimer.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (autoSearchTimer.current) clearTimeout(autoSearchTimer.current);
    };
  }, []);

  useLayoutEffect(() => {
    const previous = previousSearchRef.current;
    const scopeChanged =
      searchIntent !== previous.searchIntent ||
      searchBounds !== previous.searchBounds ||
      searchLocationMode !== previous.searchLocationMode;
    if (!scopeChanged && onBoundsChange === previous.onBoundsChange) return;

    previousSearchRef.current = { searchIntent, searchBounds, searchLocationMode, onBoundsChange };
    if (autoSearchTimer.current) {
      clearTimeout(autoSearchTimer.current);
      autoSearchTimer.current = null;
    }
    const submittedArea =
      searchIntent === previous.searchIntent &&
      searchLocationMode === "viewport" &&
      searchBounds === submittedBoundsRef.current;
    submittedBoundsRef.current = null;
    if (!scopeChanged || submittedArea) return;

    shouldFitNextPins.current = true;
    setCurrentBounds(null);
    setShowSearchArea(false);
    setSelectedVenueId(null);
    setSelectedCluster(null);
  }, [searchIntent, searchBounds, searchLocationMode, onBoundsChange]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: a changed intent must fit even when cached pins are unchanged
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !shouldFitNextPins.current) return;

    const bbox =
      searchLocationMode === "viewport" && searchBounds
        ? parseBounds(searchBounds)
        : computeInitialFitBbox(pins, searchLocationMode, searchLat, searchLng);
    if (!bbox) return;

    shouldFitNextPins.current = false;
    map.fitBounds(
      [
        [bbox[0], bbox[1]],
        [bbox[2], bbox[3]],
      ],
      searchLocationMode === "viewport" && searchBounds
        ? { duration: FIT_DURATION }
        : { padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM, duration: FIT_DURATION },
      { searchFit: true },
    );
    // Starting a fit can synchronously finish an earlier user move.
    if (autoSearchTimer.current) {
      clearTimeout(autoSearchTimer.current);
      autoSearchTimer.current = null;
    }
  }, [pins, mapLoaded, searchIntent, searchBounds, searchLocationMode, searchLat, searchLng]);

  const geoJsonPoints = useMemo(
    () =>
      pins
        .filter(
          (pin): pin is Pin & { lat: number; lng: number } => pin.lat != null && pin.lng != null,
        )
        .map((pin) => ({
          type: "Feature" as const,
          properties: { id: pin.id, name: pin.name },
          geometry: {
            type: "Point" as const,
            coordinates: [pin.lng, pin.lat] as [number, number],
          },
        })),
    [pins],
  );

  const mapBounds = useMemo((): [number, number, number, number] | null => {
    if (currentBounds) return parseBounds(currentBounds);
    return computeBboxFromPins(pins);
  }, [currentBounds, pins]);

  const { clusters, supercluster } = useSupercluster(
    geoJsonPoints,
    mapBounds,
    viewState.zoom ?? 2,
    {
      radius: CLUSTER_RADIUS,
      maxZoom: CLUSTER_MAX_ZOOM,
    },
  );

  const handleMoveEnd = useCallback(
    (evt: {
      viewState: { latitude: number; longitude: number; zoom: number };
      searchFit?: boolean;
      target: {
        getBounds: () => { _ne: { lat: number; lng: number }; _sw: { lat: number; lng: number } };
      };
    }) => {
      const bounds = evt.target.getBounds();
      const boundsStr = serializeBounds(bounds);
      const resized = resizeMoveEnd.current;
      resizeMoveEnd.current = false;

      const drift = computeViewportDrift(
        { lat: evt.viewState.latitude, lng: evt.viewState.longitude },
        searchLat,
        searchLng,
        searchBounds,
      );

      startTransition(() => {
        setCurrentBounds(boundsStr);
        setShowSearchArea(drift > VIEWPORT_DRIFT_THRESHOLD);
      });

      if (autoSearch && !evt.searchFit && !resized) {
        if (autoSearchTimer.current) clearTimeout(autoSearchTimer.current);
        autoSearchTimer.current = setTimeout(() => {
          autoSearchTimer.current = null;
          submittedBoundsRef.current = boundsStr;
          onBoundsChange(boundsStr);
          setShowSearchArea(false);
        }, AUTO_SEARCH_DEBOUNCE_MS);
      }
    },
    [searchLat, searchLng, searchBounds, autoSearch, onBoundsChange],
  );

  const handleSearchAreaClick = useCallback(() => {
    if (autoSearchTimer.current) {
      clearTimeout(autoSearchTimer.current);
      autoSearchTimer.current = null;
    }
    if (currentBounds) {
      submittedBoundsRef.current = currentBounds;
      onBoundsChange(currentBounds);
      setShowSearchArea(false);
    }
  }, [currentBounds, onBoundsChange]);

  const markerClickedRef = useRef(false);

  const handleClusterClick = useCallback(
    (clusterId: number, lat: number, lng: number) => {
      if (!supercluster) return;
      markerClickedRef.current = true;
      setSelectedVenueId(null);
      const expansionZoom = supercluster.getClusterExpansionZoom(clusterId);
      setSelectedCluster(
        expansionZoom > CLUSTER_MAX_ZOOM
          ? {
              latitude: lat,
              longitude: lng,
              pinIds: new Set(
                supercluster
                  .getLeaves(clusterId, Infinity)
                  .map((leaf) => leaf.properties.id as string),
              ),
            }
          : null,
      );
      setViewState({
        longitude: lng,
        latitude: lat,
        zoom: Math.min(expansionZoom, CLUSTER_MAX_ZOOM),
      });
    },
    [supercluster],
  );

  const handleMarkerSelect = useCallback((id: string) => {
    markerClickedRef.current = true;
    setSelectedCluster(null);
    setSelectedVenueId((prev) => (prev === id ? null : id));
  }, []);

  const handleMapClick = useCallback(() => {
    if (markerClickedRef.current) {
      markerClickedRef.current = false;
      return;
    }
    setSelectedVenueId(null);
    setSelectedCluster(null);
  }, []);

  const clusterPins = selectedCluster
    ? pins.filter((pin) => selectedCluster.pinIds.has(pin.id))
    : [];

  const selectedPin = selectedVenueId
    ? pins.find((p) => p.id === selectedVenueId && p.lat != null && p.lng != null)
    : null;
  const selectedVenue: VenueCard | null = selectedVenueId
    ? (venues.find((v) => v.id === selectedVenueId) ??
      (selectedPin
        ? {
            id: selectedPin.id,
            name: selectedPin.name,
            slug: selectedPin.slug,
            tagline: null,
            primary_category_key: null,
            primary_category_name: null,
            category_names: [],
            city: null,
            rating: null,
            review_count: null,
            cover_image_url: null,
            gallery_urls: [],
            distance_km: null,
            location:
              selectedPin.lat != null && selectedPin.lng != null
                ? { lat: selectedPin.lat, lng: selectedPin.lng }
                : null,
            inside_selected_area: selectedPin.inside_selected_area,
          }
        : null))
    : null;

  return (
    <div className="relative h-full w-full" data-testid="map-panel">
      <MapGL
        key={mapAttempt}
        ref={mapRef}
        {...viewState}
        onMove={(evt) => setViewState(evt.viewState)}
        onMoveEnd={handleMoveEnd}
        onResize={(event) => {
          // An idle resize immediately emits its own moveend; an active move keeps its own end.
          resizeMoveEnd.current = !event.target.isMoving();
        }}
        onClick={handleMapClick}
        onLoad={() => {
          setMapError((current) => (current === "unavailable" ? null : current));
          setMapLoaded(true);
        }}
        onError={(event) => {
          setMapError(event.target?.getStyle() ? "details" : "unavailable");
        }}
        mapStyle={MAP_STYLE}
        workerUrl={workerUrl}
        style={{ width: "100%", height: "100%" }}
      >
        <VenueMarkers
          clusters={clusters}
          hoveredVenueId={hoveredVenueId}
          selectedVenueId={selectedVenueId}
          onHoverMarker={onHoverMarker}
          onMarkerClick={handleMarkerSelect}
          onClusterClick={handleClusterClick}
        />

        {selectedCluster && (
          <Popup
            longitude={selectedCluster.longitude}
            latitude={selectedCluster.latitude}
            anchor="bottom"
            offset={[0, -28] as [number, number]}
            closeButton
            closeOnClick={false}
            focusAfterOpen
            onClose={() => setSelectedCluster(null)}
            maxWidth="none"
            className="venue-popup"
          >
            <ul
              aria-label="Salons at this location"
              className="m-0 max-h-64 w-[280px] list-none overflow-y-auto overscroll-y-contain rounded-xl bg-surface p-1 pt-7 shadow-soft2"
            >
              {clusterPins.map((pin) => (
                <li key={pin.id}>
                  <button
                    type="button"
                    className="w-full rounded-md px-3 py-3 text-left text-[14px] font-medium text-ink hover:bg-fog focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-electric"
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedCluster(null);
                      setSelectedVenueId(pin.id);
                    }}
                  >
                    {pin.name}
                  </button>
                </li>
              ))}
            </ul>
          </Popup>
        )}

        {selectedPin && selectedVenue && selectedPin.lat != null && selectedPin.lng != null && (
          <Popup
            longitude={selectedPin.lng}
            latitude={selectedPin.lat}
            anchor="bottom"
            offset={[0, -28] as [number, number]}
            closeButton={false}
            closeOnClick={false}
            maxWidth="none"
            className="venue-popup"
          >
            <VenuePopupCard venue={selectedVenue} onClose={() => setSelectedVenueId(null)} />
          </Popup>
        )}

        <NavigationControl position="bottom-right" />
      </MapGL>
      {isLoading && !mapError && (
        <div
          role="status"
          className="absolute inset-x-3 bottom-12 z-10 bg-surface px-4 py-3 text-center text-[14px] text-slate shadow-soft1"
        >
          Searching salons…
        </div>
      )}
      {mapError && (
        <div
          role="alert"
          className="absolute inset-x-3 bottom-12 z-10 flex flex-wrap items-center justify-center gap-3 bg-surface px-4 py-3 text-[14px] shadow-soft1"
        >
          <p className="text-danger">
            {mapError === "details"
              ? "Some map details could not load."
              : "Map unavailable. You can still browse the list."}
          </p>
          <button
            type="button"
            className="font-semibold text-electric-800 underline underline-offset-4"
            onClick={() => {
              setMapError(null);
              setMapLoaded(false);
              setMapAttempt((attempt) => attempt + 1);
            }}
          >
            Retry map
          </button>
        </div>
      )}
      <SearchAreaButton
        visible={showSearchArea}
        autoSearch={autoSearch}
        onAutoSearchChange={handleAutoSearchChange}
        onClick={handleSearchAreaClick}
        className={searchAreaClassName}
      />
    </div>
  );
}

export { MapPanel };
